import WarningIcon from "@mui/icons-material/WarningAmber";
import { Stack, TableCell, Tooltip, Typography } from "@mui/material";

import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { DEFENDANT_COLUMN_MIN_WIDTH } from "./ClaimTableHead";
import { Party } from "./IssueModel";
import { WinChanceSlider } from "./WinChanceSlider";

/**
 * One defendant's win-chance slider. Whether the defendant is on the issue at
 * all, and whether a chance has been entered for them, are decided by the
 * caller through the shared win-chance predicates, so this cell cannot drift
 * from the analytics gate: a missing chance is marked, never read as 0%.
 */
export const WinChanceCell = ({
  defendant,
  winChance,
  involved,
  isChanceMissing,
  onWinChanceUpdated,
  limit = 1,
}: {
  defendant: Party;
  winChance?: number;
  involved: boolean;
  isChanceMissing: boolean;
  onWinChanceUpdated: (defendantId: string, newChance: number) => void;
  limit?: number;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();

  return (
    <TableCell
      sx={{ borderBottom: "none", minWidth: DEFENDANT_COLUMN_MIN_WIDTH }}
    >
      {!involved ? (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("case.claims.winChance.notInvolved")}
        </Typography>
      ) : (
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: theme.spacing(0.75),
          }}
        >
          {isChanceMissing && (
            <Tooltip title={t("case.prerequisites.winChanceMissing")} arrow>
              <WarningIcon
                fontSize="small"
                sx={{ color: theme.palette.warning.main }}
              />
            </Tooltip>
          )}
          <WinChanceSlider
            chance={winChance ?? 0}
            onChanceChange={(newChance) =>
              onWinChanceUpdated(defendant.id, newChance)
            }
            opponentName={defendant.name}
            limit={limit}
          />
        </Stack>
      )}
    </TableCell>
  );
};
